import { useState, useEffect } from 'react';

import DataSourceListIcon from './DataSourceListIcon';
import IngestionStatusLabel from '../components/IngestionStatusLabel/IngestionStatusLabel';
import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import { IActiveIngestion } from 'Modules/Studio/components/StudioInfo/redux/ingestion.interface';
import { ListItem } from '@3.0/List';
import { useDSIngestionStatus } from '../../hooks/useDSIngestionStatus.query';
import { ShowComponentIfUserHasPermission } from 'RBAC/index';
import { StudioRoutes } from 'Routing/RouteConstants';
import IngestionByTableButton from './components/IngestionByTableButton';
import ViewLogButton from './components/ViewLogButton';
import { TIngestionMode } from './IngestionByDataSource.constants';
import IngestionButtonByUseCase from './components/IngestionButtons/IngestionButtonByUseCase';

type TProps = {
  listItem: IDataSourceItem & { status?: number; startTime?: Date };
  showLogButton?: boolean;
  onAddDismissibleLink?: () => void;
  mode: TIngestionMode;
};

export default function DataSourceListItem({
  listItem,
  showLogButton = false,
  onAddDismissibleLink = () => {},
  mode,
}: TProps) {
  const { data: activeIngestions } = useDSIngestionStatus();
  const [activeIngestion, setActiveIngestion] = useState<IActiveIngestion | null>(null);

  useEffect(() => {
    if (activeIngestions && activeIngestions.length > 0) {
      const activeIngestion = activeIngestions.find(
        (item: IActiveIngestion) => item.dataSourceId === listItem.id
      );

      if (activeIngestion) {
        setActiveIngestion(activeIngestion);
      } else {
        setActiveIngestion(null);
      }
    } else {
      setActiveIngestion(null);
    }
  }, [activeIngestions, listItem.id]);

  return (
    <ListItem
      liProps={{
        className:
          'tw:border-b-primary-600  tw:pl-6 tw:pr-0 tw:flex tw:h-10 tw:items-center tw:justify-between',
        'data-testid': `ingest-by-ds-${listItem.id}`,
      }}
    >
      <span className="tw:flex tw:h-10 tw:flex-auto tw:items-center tw:justify-between">
        <span className="tw:flex tw:items-center">
          <DataSourceListIcon dataSourceType={listItem.dataSourceType} mode={mode} />

          <span
            title={listItem.name}
            className={`tw:ml-3 tw:text-base tw:truncate tw:font-normal ${
              listItem.status || activeIngestion?.status ? 'tw:max-w-[230px]' : 'tw:max-w-[290px]'
            }`}
          >
            {listItem.name}
          </span>
        </span>
        <span className="tw:pr-[11px] tw:h-10 tw:flex tw:items-center">
          <IngestionStatusLabel
            small
            status={activeIngestion?.status}
            activeIngestion={activeIngestion}
            latestIngestionLog={listItem}
            mode={mode}
            activeIngestionTables={activeIngestion?.tables}
          />
        </span>
      </span>

      <ShowComponentIfUserHasPermission permissionRef={StudioRoutes.ingestion.permission}>
        <IngestionButtonByUseCase
          mode={mode}
          listItem={listItem}
          activeIngestion={activeIngestion}
        />
        <IngestionByTableButton dataSource={listItem} activeIngestion={activeIngestion} />
      </ShowComponentIfUserHasPermission>
      {showLogButton && (
        <ViewLogButton showTitle={false} onAddDismissibleLink={onAddDismissibleLink} />
      )}
    </ListItem>
  );
}
